import React, { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';

const socials = [
  { label: 'Facebook', symbol: 'f', href: '#' },
  { label: 'Instagram', symbol: '◉', href: '#' },
  { label: 'X (Twitter)', symbol: '✕', href: '#' },
  { label: 'TikTok', symbol: '◈', href: '#' },
];

export default function Contact() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => setSent(false), 5000);
    setForm({ name: '', email: '', message: '' });
  };

  const inputStyle = {
    width: '100%',
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(201,168,76,0.15)',
    borderRadius: 6,
    padding: '14px 16px',
    color: '#F5F0E8',
    fontFamily: 'Inter',
    fontSize: 14,
    outline: 'none',
    transition: 'border-color 0.3s',
  };

  return (
    <section id="contact" ref={ref} style={{
      padding: '120px 5% 80px',
      background: 'linear-gradient(180deg, #0D0D0D 0%, #0a0806 100%)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Background glow */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '80%',
        height: 400,
        background: 'radial-gradient(ellipse, rgba(232,115,42,0.04) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          style={{ textAlign: 'center', marginBottom: 80 }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 20 }}>
            <div style={{ width: 60, height: 1, background: 'linear-gradient(to right, transparent, #C9A84C)' }} />
            <span style={{ fontFamily: 'Cinzel', fontSize: 11, letterSpacing: 4, color: '#C9A84C', textTransform: 'uppercase' }}>Begin Your Journey</span>
            <div style={{ width: 60, height: 1, background: 'linear-gradient(to left, transparent, #C9A84C)' }} />
          </div>
          <h2 style={{
            fontFamily: 'Cinzel',
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            color: '#F5F0E8',
            letterSpacing: 2,
            marginBottom: 16,
          }}>
            Connect With Us
          </h2>
          <p style={{ maxWidth: 500, margin: '0 auto', color: '#B0A898', lineHeight: 1.8 }}>
            Every great project begins with a conversation. Let's discover how Vedic IT can bring clarity and purpose to your digital journey.
          </p>
        </motion.div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 60,
          alignItems: 'start',
        }}>
          {/* Left: Info */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <h3 style={{ fontFamily: 'Cinzel', fontSize: 18, color: '#C9A84C', letterSpacing: 1, marginBottom: 32 }}>
              Reach Out
            </h3>

            {[
              { icon: '✉', label: 'Email', value: 'info@vedicit.org', href: 'mailto:info@vedicit.org' },
              { icon: '☎', label: 'Phone', value: '+91 9611701326', href: 'tel:+919611701326' },
            ].map(item => (
              <a
                key={item.label}
                href={item.href}
                style={{ textDecoration: 'none', display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 28 }}
              >
                <div style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: 'rgba(232,115,42,0.08)',
                  border: '1px solid rgba(232,115,42,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  color: '#E8732A',
                  flexShrink: 0,
                }}>
                  {item.icon}
                </div>
                <div>
                  <div style={{ fontSize: 11, letterSpacing: 2, color: '#6B6560', textTransform: 'uppercase', marginBottom: 4 }}>{item.label}</div>
                  <div style={{ color: '#F5F0E8', fontSize: 15 }}>{item.value}</div>
                </div>
              </a>
            ))}

            {/* Registered under */}
            <div style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(201,168,76,0.1)',
              borderRadius: 8,
              padding: '16px 20px',
              marginBottom: 32,
            }}>
              <div style={{ fontSize: 11, letterSpacing: 2, color: '#6B6560', textTransform: 'uppercase', marginBottom: 6 }}>Registered Under</div>
              <div style={{ fontSize: 14, color: '#B0A898' }}>Sri Arunachala Services Initiative</div>
              <div style={{ fontSize: 12, color: '#6B6560', marginTop: 4 }}>80G Non-Profit Registration</div>
            </div>

            {/* Socials */}
            <div>
              <div style={{ fontSize: 11, letterSpacing: 2, color: '#6B6560', textTransform: 'uppercase', marginBottom: 16 }}>Follow Our Journey</div>
              <div style={{ display: 'flex', gap: 12 }}>
                {socials.map(s => (
                  <motion.a
                    key={s.label}
                    href={s.href}
                    whileHover={{ scale: 1.1, borderColor: 'rgba(232,115,42,0.6)' }}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      border: '1px solid rgba(201,168,76,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#B0A898',
                      fontSize: 13,
                      textDecoration: 'none',
                      transition: 'all 0.3s',
                    }}
                    title={s.label}
                  >
                    {s.symbol}
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <label style={{ fontSize: 11, letterSpacing: 2, color: '#6B6560', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
                  Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Your name"
                  required
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'rgba(201,168,76,0.5)'}
                  onBlur={e => e.target.style.borderColor = 'rgba(201,168,76,0.15)'}
                />
              </div>
              <div>
                <label style={{ fontSize: 11, letterSpacing: 2, color: '#6B6560', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
                  Email
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="your@email.com"
                  required
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'rgba(201,168,76,0.5)'}
                  onBlur={e => e.target.style.borderColor = 'rgba(201,168,76,0.15)'}
                />
              </div>
              <div>
                <label style={{ fontSize: 11, letterSpacing: 2, color: '#6B6560', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
                  Message
                </label>
                <textarea
                  value={form.message}
                  onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  placeholder="Tell us about your project..."
                  required
                  rows={5}
                  style={{ ...inputStyle, resize: 'vertical' }}
                  onFocus={e => e.target.style.borderColor = 'rgba(201,168,76,0.5)'}
                  onBlur={e => e.target.style.borderColor = 'rgba(201,168,76,0.15)'}
                />
              </div>

              <motion.button
                type="submit"
                whileHover={{ scale: 1.02, boxShadow: '0 0 30px rgba(232,115,42,0.4)' }}
                whileTap={{ scale: 0.98 }}
                style={{
                  background: 'linear-gradient(135deg, #E8732A, #C4581A)',
                  border: 'none',
                  borderRadius: 6,
                  padding: '16px',
                  color: 'white',
                  fontFamily: 'Cinzel',
                  fontSize: 13,
                  letterSpacing: 3,
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                }}
              >
                Send Message
              </motion.button>

              <AnimatePresence>
                {sent && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    style={{
                      background: 'rgba(201,168,76,0.08)',
                      border: '1px solid rgba(201,168,76,0.3)',
                      borderRadius: 6,
                      padding: '14px',
                      textAlign: 'center',
                      color: '#C9A84C',
                      fontSize: 13,
                      fontFamily: 'Cinzel',
                      letterSpacing: 1,
                    }}
                  >
                    ✓ Message received. We will be in touch soon.
                  </motion.div>
                )}
              </AnimatePresence>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
